import { useEffect, useRef } from 'react';

const BUFFERING: number = parseInt(window.env.VITE_SLDP_BUFFERING || '150');
const LATENCY_TOLERANCE: number = parseInt(window.env.VITE_SLDP_LATENCY_TOLERANCE || '450');

type Sldp = {
  init: (playerOptions: SldpOptions) => SldpPlayer;
  isMediaSourceSupported: () => boolean;
};

export type SldpPlayer = {
  destroy: (callback?: (args?: unknown) => void) => void;
  refreshVUMeterUI: () => void;
};

type SldpOptions = {
  container: string;
  stream_url: string | undefined;
  autoplay?: boolean;
  muted?: boolean;
  video_only?: boolean;
  muteable?: boolean;
  width: string | number;
  height: string | number;
  reconnects?: number;
  controls?: boolean;
  adaptive_bitrate?: boolean;
  pause_timeout?: number;
  buffering?: number;
  latency_tolerance?: number;
};

let SLDP: Sldp;
if ('SLDP' in window) {
  SLDP = window.SLDP as Sldp;
} else {
  throw new Error('SLDP failed to initialize');
}

function getContainerFromId(containerId: string) {
  const parent = document.getElementById(containerId);
  if (parent === null) {
    throw new Error('parent not found while setting up an SLDP player');
  }
  return parent;
}

function removeNoPlayableSourceMessage(parent: HTMLElement) {
  parent.querySelectorAll('.sldp_message_wrp').forEach((element) => {
    element.remove();
  });
}

function initPlayer(containerId: string, streamUrl: string | undefined): SldpPlayer {
  try {
    return SLDP.init({
      container: containerId,
      stream_url: streamUrl,
      autoplay: true,
      muted: true,
      video_only: true,
      muteable: false,
      width: 'parent',
      height: 'parent',
      reconnects: 10,
      controls: false,
      adaptive_bitrate: true,
      pause_timeout: 60,
      buffering: BUFFERING,
      latency_tolerance: LATENCY_TOLERANCE, // for being non-tolerant to increasing latency
    });
  } catch (e) {
    throw new Error('cannot init sldp player', { cause: e });
  }
}

function destroyPlayer(player: SldpPlayer | null) {
  if (!player) {
    return;
  }
  try {
    player.destroy();
  } catch (e) {
    console.error('[Player] error while destroying player', e);
  }
}

export function usePlayer(containerId: string, streamUrl?: string, isPaused?: boolean) {
  const player = useRef<SldpPlayer | null>(null);

  useEffect(() => {
    if (isPaused) {
      return;
    }

    const parent = getContainerFromId(containerId);
    removeNoPlayableSourceMessage(parent);
    player.current = initPlayer(containerId, streamUrl);

    return () => {
      destroyPlayer(player.current);
      player.current = null;
    };
  }, [containerId, streamUrl, isPaused]);
}
